import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import SiteEnum from '@models/enums/SiteEnum';
import { IVideo } from '@models/IVideo';
import { IVideoAttributes } from '@models/IVideoAttributes';
import TextModal, { IModalLabels } from '@molecules/TextModal/TextModal';
import useOneTrustConsent from '@utility/hooks/useOneTrustConsent';
import classNames from 'classnames';
import { AnimatePresence, motion, useInView } from 'motion/react';
import { ForwardedRef, forwardRef, useCallback, useRef, useState } from 'react';
import ReactPlayer, { Config } from 'react-player';

interface Props extends IVideo {
  readonly classes?: string;
  readonly config?: Config;
  readonly controls?: boolean;
  readonly isPortrait?: boolean;
  readonly modalLabels?: IModalLabels;
  readonly site?: SiteEnum.B2B;
  readonly videoAttributes?: IVideoAttributes;
}

const Video = forwardRef(
  (
    {
      classes,
      config,
      controls = true,
      isPortrait,
      poster,
      videoAttributes,
      videoSource,
      modalLabels,
      site = SiteEnum.B2B,
    }: Props,
    ref
  ) => {
    const isYouTube = videoSource.includes('youtube.com');

    const [isPlaying, setIsPlaying] = useState(!!videoAttributes?.playing);
    const [showVideo, setShowVideo] = useState(!isYouTube);
    const [showPoster, setShowPoster] = useState(!videoAttributes?.playing);
    const [modalOpen, setModalOpen] = useState(false);

    const videoRef = useRef<HTMLDivElement>(null);
    const playButtonRef = useRef<HTMLButtonElement>(null!);
    const inView = useInView(videoRef, { once: true });

    if (!videoSource) return null;

    const selectedCookies = useOneTrustConsent(videoSource);

    const handlePlay = () => {
      const hasTargetingConsent = selectedCookies.includes('Targeting');

      if (isYouTube && !hasTargetingConsent) {
        setModalOpen(true);
        return;
      }

      setIsPlaying(true);
      setShowPoster(false);
      setShowVideo(true);
    };

    const handleEnd = () => {
      setIsPlaying(false);
      setShowPoster(true);
    };

    const handleOpenCookieSettings = useCallback(() => {
      setModalOpen(false);
      window.OneTrust?.ToggleInfoDisplay();
    }, []);

    return (
      <div
        ref={ref as ForwardedRef<HTMLDivElement>}
        className={
          classes ??
          classNames(
            isPortrait ? 'tw-aspect-[4/5]' : 'tw-aspect-video',
            'tw-relative tw-flex tw-h-auto tw-w-full tw-min-w-full tw-items-center tw-justify-center'
          )
        }
      >
        <div ref={videoRef} className="tw-relative tw-h-full tw-w-full">
          <AnimatePresence mode="wait">
            {!!poster && showPoster ? (
              <motion.div
                key="poster"
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <Image
                  image={poster}
                  classes="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center"
                  imageClasses="tw-object-cover tw-w-full tw-h-full"
                >
                  <div className="tw-absolute">
                    <Button
                      ref={playButtonRef}
                      onClick={handlePlay}
                      variant="white"
                      iconName="play"
                      buttonClasses="tw-text-4xl !tw-h-2xl tw-w-2xl tw-p-0 hover:!tw-bg-black tw-group"
                      iconClasses="tw-translate-x-[3px] group-hover:tw-text-white"
                      iconSize="2rem"
                      aria-label="Play"
                    />
                  </div>
                </Image>
              </motion.div>
            ) : (
              <motion.div
                key="video"
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="tw-h-full tw-w-full"
              >
                {inView && showVideo && (
                  <ReactPlayer
                    className="[&>video]:tw-object-cover"
                    url={videoSource}
                    width="100%"
                    height="100%"
                    controls={controls}
                    playing={isPlaying}
                    config={config}
                    onEnded={handleEnd}
                    {...videoAttributes}
                  />
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <TextModal
          labels={
            modalLabels ?? {
              title: 'View video',
              buttonLabel: 'Accept cookies here',
              description:
                'To watch the video you must accept marketing cookies for this website',
            }
          }
          isOpen={modalOpen}
          onButtonClick={handleOpenCookieSettings}
          onClose={() => setModalOpen(false)}
          restoreFocusRef={playButtonRef}
          setIsOpen={setModalOpen}
          site={site}
        />
      </div>
    );
  }
);

Video.displayName = 'Video';

export default Video;
